import { FC, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { MessageSquare, Maximize2, Image } from "lucide-react";
import { note } from "@/endpoints/dryp/note";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { Skeleton } from "@/components/ui/skeleton";
import { AddNote } from "./AddNote";
import { useStore } from "@/hooks/useStore";
import { Note } from "./Note/Note";
import { NotesModal } from "./NotesModal";
import { PhotoGalleryModal } from "./PhotoGalleryModal";
import { MEDIA_TYPES } from "@/config/media";

type NoteProps = {
	structureId: Dryp.StructureId;
	inline?: boolean;
};

const INITIAL_VISIBLE = 3;

export const Notes: FC<NoteProps> = observer(({ structureId, inline = false }) => {
	const { userStore } = useStore();
	const noteApi = useAsyncStateStore(note.getAll);
	const [showAll, setShowAll] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isGalleryOpen, setIsGalleryOpen] = useState(false);

	useEffect(() => {
		noteApi.fetch(structureId);
	}, [structureId]);

	const notes = [...(noteApi.data || [])].sort((a, b) => b.createdAt - a.createdAt);
	const photoNotes = notes.filter(n => n.mediaType === MEDIA_TYPES.IMAGE);
	const canDelete = userStore.canEdit;
	const userName = userStore.getUser?.name;

	const handleDeleteNote = async (noteId: Dryp.NoteId) => {
		await note.deleteNote(noteId);
		const updated = (noteApi.data || []).filter(n => n.id !== noteId);
		noteApi.updateData(updated);
	};

	const addNote = (note: Dryp.Note) => {
		const notes = noteApi.data || [];
		const adjustedNote = note.createdBy
			? note
			: ({
					...note,
					createdBy: userName,
				} as Dryp.Note);
		noteApi.updateData([adjustedNote, ...notes]);
	};

	const visibleNotes = inline && !showAll ? notes.slice(0, INITIAL_VISIBLE) : notes;
	const hiddenCount = notes.length - INITIAL_VISIBLE;

	if (inline) {
		return (
			<>
				<div className="rounded-xl border border-border bg-card">
					<div className="p-4 pb-3 flex items-center justify-between">
						<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<MessageSquare className="w-4 h-4" />
							Notes
						</h3>
						<div className="flex items-center gap-2">
							{photoNotes.length > 0 && (
								<button
									onClick={() => setIsGalleryOpen(true)}
									className="inline-flex items-center text-xs h-6 gap-1 rounded-md px-2 border border-border bg-transparent hover:bg-accent"
								>
									<Image className="w-3 h-3" />
									{photoNotes.length} photos
								</button>
							)}
							<button
								onClick={() => setIsModalOpen(true)}
								className="inline-flex items-center justify-center h-7 w-7 p-0 rounded-md hover:bg-accent"
							>
								<Maximize2 className="w-3.5 h-3.5" />
							</button>
						</div>
					</div>
					<div className="px-4 pb-4 space-y-4">
						<AddNote structureId={structureId} onPosted={addNote} />

						{noteApi.loading && (
							<div className="space-y-4">
								<Skeleton className="h-12 w-full" />
								<Skeleton className="h-12 w-full" />
							</div>
						)}

						{noteApi.error && <div className="text-sm text-red-500">Failed to load notes</div>}

						{!noteApi.loading && !noteApi.error && notes.length === 0 && (
							<div className="text-muted-foreground text-sm">No notes yet.</div>
						)}

						{notes.length > 0 && (
							<div className={`relative ${showAll ? "max-h-[400px] overflow-y-auto pr-2" : ""}`}>
								<div className="absolute left-[9px] top-3 bottom-3 w-px bg-border z-0" />
								<div className="space-y-4">
									{visibleNotes.map(n => (
										<Note key={n.id} note={n} canDelete={canDelete} onDelete={handleDeleteNote} />
									))}
								</div>
							</div>
						)}

						{hiddenCount > 0 && !showAll && (
							<button
								onClick={() => setShowAll(true)}
								className="w-full text-xs h-8 text-muted-foreground hover:bg-accent rounded-md"
							>
								Show {hiddenCount} more entries
							</button>
						)}

						{showAll && notes.length > INITIAL_VISIBLE && (
							<button
								onClick={() => setShowAll(false)}
								className="w-full text-xs h-8 text-muted-foreground hover:bg-accent rounded-md"
							>
								Show less
							</button>
						)}
					</div>
				</div>
				{isModalOpen && (
					<NotesModal
						structureId={structureId}
						notes={notes}
						loading={noteApi.loading}
						error={noteApi.error}
						canDelete={canDelete}
						onClose={() => setIsModalOpen(false)}
						onNoteAdded={addNote}
						onNoteDeleted={handleDeleteNote}
					/>
				)}
				{isGalleryOpen && photoNotes.length > 0 && (
					<PhotoGalleryModal photos={photoNotes} onClose={() => setIsGalleryOpen(false)} />
				)}
			</>
		);
	}

	return (
		<div className="flex h-full flex-col">
			<div className="sticky top-0 z-10 bg-background px-4 py-2">
				<h2 className="text-gray-900">Structure notes</h2>
			</div>
			<div className="flex-1 px-4 pt-3 overflow-auto">
				{noteApi.loading && (
					<div className="space-y-4">
						<Skeleton className="h-8 w-2/3" />
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-5/6" />
					</div>
				)}

				{noteApi.error && (
					<div className="text-sm text-red-500">Failed to load notes: {noteApi.error.message}</div>
				)}

				{!noteApi.loading && !noteApi.error && notes.length === 0 && (
					<div className="text-muted-foreground text-sm">No notes yet created.</div>
				)}

				{notes.length > 0 && (
					<div className="relative">
						<div className="absolute left-[9px] top-3 bottom-3 w-px bg-border z-0" />
						<div className="space-y-4">
							{notes.map(n => (
								<Note key={n.id} note={n} canDelete={canDelete} onDelete={handleDeleteNote} />
							))}
						</div>
					</div>
				)}
			</div>

			<div className="sticky bottom-0 z-10 bg-background px-4 pb-4">
				<AddNote structureId={structureId} onPosted={addNote} />
			</div>
		</div>
	);
});
